import type { CSSProperties } from 'react';

import { useMemo } from 'react';

type PlayerColors = {
  theme?: 'dark' | 'light';
  customPrimaryColor?: string;
  controlBarColor?: string;
  controlBarOpacity?: number;
  iconColor?: string;
  bigPlayButtonIconColor?: string;
};

export const COLORS = {
  white: 'rgb(255 255 255)',
  black: 'rgb(0 0 0)',
  yellow: 'rgb(255 255 0)',
  green: 'rgb(0 255 0)',
  cyan: 'rgb(0 255 255)',
  blue: 'rgb(0 0 255)',
  magenta: 'rgb(255 0 255)',
  red: 'rgb(255 0 0)',
};

export const DARK_THEME_COLORS = {
  '--primaryColor': 'rgb(0 147 57)',
  '--backgroundColor': 'rgb(32 32 32)',
  '--videoBackground': 'rgb(0 0 0)',
  '--textColor': 'rgb(255 255 255)',
  '--sliderBackgroundColor': 'rgb(255 255 255 / 0.2)',
  '--labelOverlayColor': 'rgb(32 32 32 / 0.5)',
  '--overlayForeground': 'rgb(255 255 255)',
  '--borderColor': 'rgb(124 124 124)',
  '--hoverBackground': 'rgb(51 51 51)',
  '--controlbarBackgroundColor': 'rgb(32 32 32)',
  '--controlbarBackgroundOpacity': 0,
};

export const LIGHT_THEME_COLORS = {
  '--primaryColor': 'rgb(0 147 57)',
  '--backgroundColor': 'rgb(255 255 255)',
  '--videoBackground': 'rgb(238 238 238)',
  '--textColor': 'rgb(32 32 32)',
  '--sliderBackgroundColor': 'rgb(255 255 255 / 0.2)',
  '--labelOverlayColor': 'rgb(32 32 32 / 0.5)',
  '--overlayForeground': 'rgb(51 51 51)',
  '--borderColor': 'rgb(196 196 196)',
  '--hoverBackground': 'rgb(245 245 245)',
  '--controlbarBackgroundColor': 'rgb(255 255 255)',
  '--controlbarBackgroundOpacity': 0,
};

export function usePlayerColorsStyles({
  theme,
  customPrimaryColor,
  controlBarColor,
  controlBarOpacity,
  iconColor,
  bigPlayButtonIconColor,
}: PlayerColors) {
  const styles = useMemo(() => {
    const themeColors = theme === 'light' ? LIGHT_THEME_COLORS : DARK_THEME_COLORS;

    return {
      '--primaryColor': customPrimaryColor || themeColors['--primaryColor'],
      '--backgroundColor': themeColors['--backgroundColor'],
      '--videoBackground': themeColors['--videoBackground'],
      '--textColor': themeColors['--textColor'],
      '--sliderBackgroundColor': themeColors['--sliderBackgroundColor'],
      '--labelOverlayColor': themeColors['--labelOverlayColor'],
      '--controlbarBackgroundColor': controlBarColor || themeColors['--controlbarBackgroundColor'],
      '--controlbarBackgroundOpacity': controlBarOpacity ?? themeColors['--controlbarBackgroundOpacity'],
      '--overlayForeground': iconColor || themeColors['--overlayForeground'],
      '--bigPlayButtonIconColor': bigPlayButtonIconColor || iconColor || themeColors['--overlayForeground'],
      '--borderColor': themeColors['--borderColor'],
      '--hoverBackground': themeColors['--hoverBackground'],
    } as CSSProperties;
  }, [controlBarColor, controlBarOpacity, customPrimaryColor, iconColor, bigPlayButtonIconColor, theme]);

  return styles;
}
